import { useCallback, useEffect, useState } from 'react';
import { Route, Routes } from 'react-router-dom';
import { Shell, type ShellContext } from './components/Shell';
import { PhasePending } from './components/PhasePending';
import { Overview } from './pages/Overview';
import { Operations } from './pages/Operations';
import { Motor } from './pages/Motor';
import { Controls, RECORTES } from './pages/Controls';
import { MasterSwitch } from './pages/MasterSwitch';
import { Titular } from './pages/Titular';
import { Attention } from './pages/Attention';
import { ContentBoard } from './pages/ContentBoard';
import { ContentDetail } from './pages/ContentDetail';
import { Integrations } from './pages/Integrations';
import { Renders } from './pages/Renders';
import { Episodes } from './pages/Episodes';
import { Learning } from './pages/Learning';
import { Experiments } from './pages/Experiments';
import { Audience } from './pages/Audience';
import { AudienceLists } from './pages/AudienceLists';
import { AudienceImport } from './pages/AudienceImport';
import { PaidMedia } from './pages/PaidMedia';
import { PaidMediaPlan } from './pages/PaidMediaPlan';
import { Community } from './pages/Community';
import { Commercial } from './pages/Commercial';
import { Executive } from './pages/Executive';
import { Login } from './pages/Login';
import { NAV_AREAS, NAV_RODAPE, type SystemState } from './lib/navigation';
import {
  api,
  ApiError,
  type ConfigurationResponse,
  type KpisResponse,
  type MeResponse,
  type OperationsResponse,
} from './lib/api';

/**
 * Raiz do Back Office.
 *
 * Fase 0 e SOMENTE LEITURA de projecoes autorizadas (Master SPEC §9: "read-only
 * system health; no live admin commands"). Nao existe botao que dispare comando
 * ao vivo nesta fase, e as areas sem dado dizem em que fase entram.
 */

type LoadState =
  | { kind: 'loading' }
  /*
    Estado PROPRIO para "sem sessao", separado de `error`.

    Tratar 401 como erro generico foi o que produziu a tela que o dono viu:
    "Nao foi possivel carregar / Autenticacao necessaria", sem nenhum caminho
    para entrar. Falta de sessao nao e falha — e o estado inicial de quem
    ainda nao entrou.
  */
  | { kind: 'anonimo' }
  | { kind: 'error'; code: string; message: string; correlationId: string | null }
  | {
      kind: 'ready';
      operations: OperationsResponse;
      configuration: ConfigurationResponse;
      kpis: KpisResponse;
      version: { app_version: string; git_sha: string; spec_version: string };
      /** Quem esta autenticado, com os escopos que o SERVIDOR resolveu. */
      me: MeResponse;
    };

export function App() {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  /**
   * Recarrega a projecao de configuracao.
   *
   * "Salvar" nao esta pronto sem o caminho de LER de volta: sem isto a tela
   * continuaria mostrando o valor e a versao antigos depois de aplicar, e a
   * proxima tentativa mandaria `expected_revision` velha e receberia conflito.
   *
   * Recarrega SO a configuracao, e nao a tela toda: recarregar tudo fecharia o
   * detalhe aberto e apagaria a mensagem de resultado que a pessoa precisa ler.
   */
  const recarregarConfiguracao = useCallback(async () => {
    try {
      const configuration = await api.configuration();
      setState((atual) => (atual.kind === 'ready' ? { ...atual, configuration } : atual));
    } catch {
      // Falha de recarga nao desmonta o painel: o comando JA foi aplicado e o
      // resultado esta na tela. Trocar isso por um erro global apagaria o
      // resumo do que acabou de acontecer.
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // A sessao e conferida ANTES de pedir dado.
      //
      // Sem isto, quatro chamadas devolvem 401 em paralelo e a tela mostra a
      // mensagem de erro de uma delas — que foi exatamente o sintoma:
      // "Autenticacao necessaria" numa tela sem porta de entrada.
      try {
        await api.me();
      } catch (error) {
        if (cancelled) return;
        if (error instanceof ApiError && error.httpStatus === 401) {
          setState({ kind: 'anonimo' });
          return;
        }
        // Qualquer outra coisa e falha de verdade e segue para o tratamento
        // de erro comum — inclusive rede fora, que nao e "nao esta logado".
      }

      try {
        const [operations, configuration, kpis, version, me] = await Promise.all([
          api.operations(),
          api.configuration(),
          api.kpis(),
          api.version(),
          api.me(),
        ]);
        if (!cancelled) setState({ kind: 'ready', operations, configuration, kpis, version, me });
      } catch (error) {
        if (cancelled) return;
        // A extracao de mensagem vive em UM lugar (lib/api.ts). Aqui apenas
        // exibimos — duplicar a regra e como o bug sobrevive ao proprio conserto.
        if (error instanceof ApiError) {
          setState({
            kind: 'error',
            code: error.code,
            message: error.message,
            correlationId: error.correlationId,
          });
        } else {
          setState({
            kind: 'error',
            code: 'NETWORK_ERROR',
            message: 'Não foi possível falar com a API do plano de controle.',
            correlationId: null,
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.kind === 'anonimo') {
    // Ao entrar, recarrega do zero: e o caminho mais simples que garante que
    // todo estado derivado da sessao nova seja buscado de novo.
    return <Login aoEntrar={() => window.location.reload()} />;
  }

  if (state.kind === 'loading') {
    return (
      <main className="p-6 text-sm" aria-busy="true">
        <p>Carregando o plano de controle…</p>
      </main>
    );
  }

  if (state.kind === 'error') {
    return (
      <main className="max-w-xl p-6 text-sm">
        <h1 className="text-lg font-semibold">Não foi possível carregar</h1>
        {/* Mensagem para a PESSOA; o codigo fica como detalhe tecnico. */}
        <p className="mt-2">{state.message}</p>
        <p className="mt-2 text-xs text-ink-faint">
          Código: {state.code}
          {state.correlationId ? ` · correlação: ${state.correlationId}` : ''}
        </p>
      </main>
    );
  }

  const { operations, configuration, kpis, version, me } = state;

  const context: ShellContext = {
    environment: operations.environment,
    systemState: operations.system_state as SystemState,
    /*
      O que a pessoa LE vem do servidor, e nao de uma conta feita aqui.

      A regra que distingue "sem pausa" de "operando" mora no
      `deriveOperationalState`; repeti-la no front criaria a mesma regra em dois
      caminhos, e a copia que envelhece e sempre a menos usada.
    */
    operationalState: operations.operational_state,
    motorEnabled: operations.motor_enabled,
    // Frescor real: null quando nenhuma projecao existe. Nunca "agora" inventado.
    dataFreshness:
      operations.projections.find((p) => p.last_processed_at !== null)?.last_processed_at ?? null,
    /*
      O nome e o papel REAIS, que o servidor ja devolvia em `/me` e a tela
      ignorava: ate 17/09 o cabecalho dizia "sessao autenticada" e "papel
      resolvido no servidor", duas frases sobre o mecanismo em vez do dado.
      O desenho entregue mostra a pessoa, e o dado existia o tempo todo.

      `roles` e uma lista; o cabecalho mostra os papeis separados por barra, e
      cai para um texto explicito quando ela vem vazia, em vez de um espaco em
      branco que parece defeito de carregamento.
    */
    actorName: me.display_name,
    actorRole: me.roles.length > 0 ? me.roles.join(' / ') : 'sem papel atribuído',
    // A UI so ESCONDE controle; a autoridade real e reavaliada em cada request.
    // Os escopos vem do SERVIDOR, e nao mais de inferencia sobre as chaves de
    // configuracao. A inferencia funcionava para configuracao e nao se
    // estendia: escopo que nao aparece naquele registro nunca era derivado, e o
    // item de menu correspondente nascia invisivel para sempre.
    scopes: me.scopes,
    appVersion: version.app_version,
  };

  return (
    <Routes>
      <Route element={<Shell context={context} />}>
        <Route
          index
          element={
            <Overview
              operations={operations}
              kpis={kpis}
              configuration={configuration}
              actorName={me.display_name}
            />
          }
        />
        <Route path="/operacoes" element={<Operations operations={operations} />} />
        {/*
          O motor busca o proprio recurso, como a caixa de atencao: uma falha na
          medicao das filas nao pode derrubar a tela de operacoes.
        */}
        <Route path="/motor" element={<Motor />} />

        {/*
          Master Switch: area 2 do layout entregue em 17/09. Recebe o mesmo
          `operations` que as vizinhas em vez de buscar de novo, porque e a
          MESMA leitura que ja alimenta o cabecalho: duas buscas poderiam
          divergir e mostrar "ON" na tela com o cabecalho dizendo "pausado".
        */}
        <Route
          path="/master-switch"
          element={
            <MasterSwitch
              operations={operations}
              systemState={context.systemState}
              motorEnabled={context.motorEnabled}
              canPause={me.scopes.includes('system:pause')}
              canPauseCategory={me.scopes.includes('category:pause')}
            />
          }
        />

        <Route
          path="/controles"
          element={<Controls configuration={configuration} onRecarregar={recarregarConfiguracao} />}
        />
        {/*
          Os dois RECORTES da mesma tela de configuracao, resposta do Ferrari de
          17/09 sobre complementar as 16 areas do layout com o que a Console
          SPEC §12 tem a mais.

          Eles reusam `Controls` com um filtro em vez de ganharem tela propria:
          "Tetos & Limites" e "Autonomia" nao sao dado novo, sao assuntos que ja
          moravam nesta grade misturados aos demais parametros. Duas copias da
          grade divergiriam, e a que envelhece e sempre a menos usada.
        */}
        <Route
          path="/controles/tetos-e-limites"
          element={
            <Controls
              configuration={configuration}
              onRecarregar={recarregarConfiguracao}
              recorte={RECORTES.tetos}
            />
          }
        />
        <Route
          path="/controles/autonomia"
          element={
            <Controls
              configuration={configuration}
              onRecarregar={recarregarConfiguracao}
              recorte={RECORTES.autonomia}
            />
          }
        />

        {/*
          Fase 1: estas areas passam a ter dado real. Elas buscam o proprio
          recurso em vez de entrar no carregamento inicial: uma falha na caixa
          de atencao nao pode derrubar a tela de operacoes, e vice-versa.
        */}
        <Route path="/atencao" element={<Attention />} />

        {/*
          Pedidos do titular (LGPD). Nao e autoatendimento: o titular nao tem
          conta aqui, e esta tela e o caminho de quem ATENDE o pedido.
        */}
        <Route path="/titular" element={<Titular />} />
        <Route path="/conteudo" element={<ContentBoard configuration={configuration} />} />
        <Route
          path="/conteudo/portfolio"
          element={<ContentBoard configuration={configuration} />}
        />
        <Route path="/conteudo/workflow" element={<ContentBoard configuration={configuration} />} />
        <Route path="/conteudo/:id" element={<ContentDetail />} />

        {/* Fase 2: descoberta, ingestao de episodio, render e medicao. */}
        <Route path="/loyalty" element={<Episodes />} />
        <Route path="/renders" element={<Renders />} />
        <Route path="/integracoes" element={<Integrations />} />

        {/* Fase 3: crescimento, audiencia, experimentacao e aprendizado. */}
        <Route path="/audiencia" element={<Audience />} />
        {/*
          Audience & Lists (Console SPEC v1.5 §177): fontes de dados, montagem
          de filtro e segmentos salvos. Busca o proprio recurso, como as
          vizinhas: uma falha na leitura das fontes nao pode derrubar a tela de
          audiencia e crescimento.
        */}
        <Route path="/audiencia/listas" element={<AudienceLists />} />
        {/*
          Importacao de audiencia por arquivo (§186, §187). Rota propria, e nao
          uma aba dentro de Audience & Lists: o arquivo e lido no navegador e a
          previa e uma conversa de varios passos com o servidor, que perderia o
          estado a cada troca de aba.
        */}
        <Route path="/audiencia/importacao" element={<AudienceImport />} />
        <Route path="/aprendizado" element={<Learning />} />
        {/*
          Fase 11: experimentos pagos (Paid Media Module SPEC v1.0 §26). Busca o
          proprio recurso como as vizinhas: uma falha na leitura dos
          experimentos nao pode derrubar a tela de aprendizado.
        */}
        <Route path="/aprendizado/experimentos" element={<Experiments />} />

        {/*
          Fase 9: mídia paga. A área é Aquisição e custos, e a tela busca o
          próprio recurso como as vizinhas: uma falha na leitura de campanhas
          não pode derrubar outra área.
        */}
        <Route path="/aquisicao" element={<PaidMedia />} />
        <Route path="/aquisicao/planos/:id" element={<PaidMediaPlan />} />

        {/* Fase 4: comunidade, moderacao e funil comercial inbound. */}
        <Route path="/comunidade" element={<Community />} />
        <Route path="/comercial" element={<Commercial />} />

        {/* Fase 5: painel executivo, custo por resultado e anomalias. */}
        <Route path="/executivo" element={<Executive />} />

        {/*
          As areas sem dado dizem isso na tela, em vez de servir um branco que
          parece defeito.

          A geracao varre as areas, o RODAPE e tambem as SUB-AREAS pendentes. As
          sub-areas entraram na varredura em 17/09 junto com o layout novo: ao
          reorganizar o menu, "Auditoria e rastreio" deixou de ser area de
          primeiro nivel e virou filha de Monitoramento. Sem este `flatMap` ela
          teria perdido a rota em silencio, e o link do menu cairia na tela de
          "pagina nao encontrada" — que e exatamente o defeito que a lista de
          areas existe para evitar.
        */}
        {[...NAV_AREAS, ...NAV_RODAPE]
          .flatMap((area) => [
            {
              ...area,
              /*
                O que a area vai mostrar: o que o cliente desenhou para ela
                quando existe, e as sub-areas quando nao. Declarar os dois
                lugares seria duas listas para a mesma pergunta.
              */
              willShow: area.willShow ?? area.children?.map((c) => c.label),
            },
            ...(area.children ?? []).map((filho) => ({ ...filho, willShow: undefined })),
          ])
          .filter((entrada) => entrada.implemented === false)
          .map((entrada) => (
            <Route
              key={entrada.id}
              path={entrada.path}
              element={
                <PhasePending
                  area={entrada.label}
                  phase={entrada.availableFromPhase}
                  reason={entrada.pendingReason ?? 'Área planejada para uma fase seguinte.'}
                  willShow={entrada.willShow}
                />
              }
            />
          ))}

        <Route
          path="*"
          element={
            /*
              `div`, e nao `main`: esta rota renderiza DENTRO do `main` da
              casca, e dois landmarks `main` aninhados deixam de dizer a um
              leitor de tela qual e o conteudo principal da pagina.
            */
            <div className="space-y-4 text-sm">
              <h1 className="text-2xl font-bold">Página não encontrada</h1>
              <p className="text-ink-muted">
                Este endereço não existe no Console. Use a navegação lateral.
              </p>
            </div>
          }
        />
      </Route>
    </Routes>
  );
}
